import { motion } from "framer-motion"
import { Heart, MessageCircle, Repeat } from "lucide-react"
import { memo } from "react"
import { LandingIconVerifiedCheck } from "../LandingIconVerifiedCheck"
import { highlightMentions } from "../utils/messageUtils"

interface BrackyReplyBubbleProps {
  message: string
  timestamp: string
  stats?: {
    comments: number
    retweets: number
    likes: number
  }
}

export const BrackyReplyBubble = memo(function BrackyReplyBubble({
  message,
  timestamp,
  stats = { comments: 3, retweets: 2, likes: 5 },
}: BrackyReplyBubbleProps) {
  return (
    <div className="relative w-[96%] sm:w-full mx-auto">
      <div className="relative backdrop-blur-md opacity-[1] rounded-[1.6rem] p-px">
        {/* Gradient glow on load */}
        <motion.div
          className="absolute inset-0 rounded-[1.1rem] bg-linear-to-br from-[#001CFF]/20 via-[#00EAFF]/10 to-transparent"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: [1, 0] }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
        />

        {/* Scanlines reveal animation overlay */}
        <motion.div
          className="absolute inset-0 scanlines rounded-[1.1rem] pointer-events-none"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: [1, 0] }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 2.75, ease: "easeOut" }}
        />

        {/* Bracky's reply bubble with inset glow */}
        <div
          className="bg-[#4ea1ff]/4 border border-[#48f3fe]/0 rounded-[1.1rem] p-4 sm:p-6"
          style={{
            boxShadow:
              "inset 0 0 20px 0 #001cff90, inset 0px 0 50px -8px #003cff, inset 0 0 20px rgba(255, 255, 255, 0.1)",
            transform: "none",
          }}
        >
          <div className="flex items-start space-x-3">
            {/* Avatar with glow */}
            <div className="relative z-100 shrink-0 -ml-1">
              {/* Bracky's Halo */}
              <span
                className="absolute inset-0 rounded-full bg-[#001CFF] pointer-events-none -z-10 transition-shadow ease-out"
                style={{
                  transform: "scale(.3)",
                  boxShadow: "0 0 90px 22px rgba(0,28,255,0.75), 0 0 200px 90px rgba(0,28,255,0.85)",
                }}
              />
              <img
                src="https://a.bracky.app/assets/landing/avatars/bracky-reply-glow-2.webp"
                alt="Bracky icon"
                className="w-9 h-9 sm:w-9 sm:h-9"
                loading="lazy"
                decoding="async"
              />
            </div>
            {/* Message content */}
            <div className="flex-1 min-w-0">
              {/* Bracky's username */}
              <div className="flex items-center space-x-[.3rem] mb-1">
                <span className="text-[#30DFFF] text-[0.9375rem] md:text-base font-bold">Bracky</span>
                <LandingIconVerifiedCheck className="relative w-4 h-4 shrink-0 text-[#0074FF]" />
                <span className="text-white/40 text-[0.9375rem] md:text-base">·</span>
                <span className="text-white/40 text-[0.9375rem] md:text-base">{timestamp}</span>
              </div>
              <div className="text-white text-[0.9375rem] sm:text-[1.0625rem] leading-[1.43] whitespace-pre-line wrap-break-word mt-[0.1rem] mb-[0.1rem]">
                {highlightMentions(message)}
              </div>
              {/* Action bar */}
              <div className="flex items-center space-x-4 mt-2 text-white/40 text-xs">
                <div className="flex items-center space-x-1">
                  <MessageCircle className="w-3.5 h-3.5" />
                  <span>{stats.comments}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Repeat className="w-3.5 h-3.5" />
                  <span>{stats.retweets}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Heart className="w-3.5 h-3.5" />
                  <span>{stats.likes}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Animated shimmer border */}
        <motion.div
          className="pointer-events-none absolute inset-0 rounded-[1.1rem]"
          style={{
            background:
              "linear-gradient(45deg, transparent 0%, transparent 45%, #001CFF 50%, #001CFF 55%, transparent 60%, transparent 100%)",
            backgroundSize: "200% 200%",
            backgroundRepeat: "no-repeat",
            WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
            padding: "2px",
            borderRadius: "inherit",
          }}
          initial={{ backgroundPosition: "-50% 0%", opacity: 0 }}
          whileInView={{ backgroundPosition: ["0% 0%", "150% 0%"], opacity: [1, 1, 0.2] }}
          viewport={{ once: true }}
          transition={{ duration: 1.5, ease: "linear", delay: 0.2 }}
        />
      </div>
    </div>
  )
})
